import React, { useMemo } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import type { Langcodes } from '@uncinc/react-drupal-core/types';
import { SearchMoreLikeThis as RSKSearchMoreLikeThis, SearchResults } from '@uncinc/react-searchkit';
import type { Results, SearchMoreLikeThisQuery, SearchMoreLikeThisQueryFilter } from '@uncinc/react-searchkit/types';

import type {
  SearchAllContentArticleHit,
  SearchAllContentHit,
} from '../../SearchAllContent/types';

import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../../config/constants';
import ArticleHit from '../MetaSearch/Results/ArticleHit';

import resultAttributes from './config';

import './style.scss';

const RenderItem = (hit: SearchAllContentHit) => {
  let type;

  if ('content_type' in hit) {
    const { content_type: contentType } = hit;
    [type] = contentType;
  } else if ('vid' in hit) {
    const { vid } = hit;
    [type] = vid;
  } else if ('profile_type' in hit) {
    const { profile_type: profileType } = hit;
    [type] = profileType;
  }

  switch (type) {
    case 'article': {
      return <ArticleHit hit={hit as SearchAllContentArticleHit} />;
    }
    default: {
      return <div />;
    }
  }
};

type ContentType = 'article';
interface SearchMoreLikeThisProps {
  entityId: number;
  langcode: Langcodes;
  fields?: string[];
  contentType: ContentType;
  maxHits?: number;
  hitsPerPage?: number;
  title?: string;
  renderItem?: Results['renderItem'];
  className?: string;
}

// TODO: This component can use some love!
// https://uncinc.monday.com/boards/99020894/pulses/8634286918

const DEFAULT_FIELDS = ['content_type', 'page_title', 'body_summarized', 'body'];
const DEFAULT_MAX_HITS = 12;
const DEFAULT_HITS_PER_PAGE = 3;

const SearchMoreLikeThis: React.FC<SearchMoreLikeThisProps> = (props) => {
  const {
    entityId,
    langcode,
    fields = DEFAULT_FIELDS,
    contentType,
    maxHits = DEFAULT_MAX_HITS,
    hitsPerPage = DEFAULT_HITS_PER_PAGE,
    title,
    renderItem = RenderItem,
    className,
  } = props;

  const { t } = useTranslation();

  const classNames = c('SearchMoreLikeThis', className);

  const [filter, query] = useMemo((): [SearchMoreLikeThisQueryFilter, SearchMoreLikeThisQuery] => ([
    {
      filter: [
        {
          term: { aggregated_content_type: { value: contentType } },
        },
        {
          term: {
            langcode,
          },
        },
      ],
    },
    {
      fields,
      like: [
        {
          _id: `entity:node/${entityId}:${langcode}`,
        },
      ],
      min_term_freq: 1,
      max_query_terms: maxHits,
    },
  ]), [contentType, entityId, fields, langcode, maxHits]);

  return (
    <div className={classNames}>
      <RSKSearchMoreLikeThis
        query={query}
        filter={filter}
        connection={{ host: SEARCHKIT_HOST_URL }}
        hitsPerPage={hitsPerPage}
        indexName={APP_OPTIONS.parameters.searchkitIndexAllContent}
        searchSettings={{
          search_attributes: [],
          result_attributes: resultAttributes,
        }}
      >
        <SearchResults
          renderItem={renderItem}
          headerElement={<h2 className="SearchMoreLikeThis__title">{title || t('searchkit.more_like_this.title')}</h2>}
          emptyState={<div />}
        />
      </RSKSearchMoreLikeThis>
    </div>
  );
};

export default SearchMoreLikeThis;
